import {
    COLLECTION_TYPE_SYMBOL,
    type Collection,
    type CollectionEntry,
    type CollectionIterateFn,
    isCollection,
} from '../collection';
import { equals } from '../equals';
import { freezeCollection, hashCollection } from '../hash';
import { isIterable, typeNameOf } from '../utils';

export type ImmutableListInit<T> = Array<T> | Collection<any, T>;

const LIST_TYPE_SYMBOL = Symbol('list');

let emptyList: undefined | ImmutableList<any>;

/**
 * Lists are ordered indexed dense collections, much like a JavaScript Array.
 * It uses a frozen JS array for native performances.
 */
export class ImmutableList<T> implements Collection<number, T> {
    public static isKeyed = false;
    public static isOrdered = true;

    private readonly _values: T[];
    public readonly size: number;

    constructor(values: Array<T>, constructorTypeCheck: typeof LIST_TYPE_SYMBOL) {
        if (constructorTypeCheck !== LIST_TYPE_SYMBOL) {
            throw new Error(
                'ImmutableList constructor is private, use ImmutableList.create() instead'
            );
        }

        this._values = values;
        this.size = values.length;
    }

    /**
     * Create a list from a set of values.
     * This function is optimized to return the same instance when initializing with an empty array.
     */
    public static create<T>(values: ImmutableListInit<T> = []): ImmutableList<T> {
        if (ImmutableList.isList(values)) {
            return values;
        }

        if (isCollection(values)) {
            values = values.toValuesArray();
        }

        if (values.length === 0) {
            emptyList = emptyList || freezeCollection(new ImmutableList([], LIST_TYPE_SYMBOL));
            return emptyList;
        }

        return freezeCollection(new ImmutableList(values, LIST_TYPE_SYMBOL));
    }

    public static isList(maybeList: any): maybeList is ImmutableList<any> {
        return isCollection(maybeList, LIST_TYPE_SYMBOL);
    }

    public get(index: number, notSetValue: T | undefined = undefined): T | undefined {
        if (notSetValue !== undefined && index >= this._values.length) {
            return notSetValue;
        }

        return this._values[index];
    }

    public getByIndex(index: number): CollectionEntry<number, T> | undefined {
        if (index < 0 || index >= this._values.length) {
            return undefined;
        }

        // @ts-ignore
        return [index, this._values[index]];
    }

    public getByKey(key: number): CollectionEntry<number, T> | undefined {
        return this.getByIndex(key);
    }

    /**
     * Replace the entire list by an array of values.
     */
    public update(values: Array<T>): this {
        return freezeCollection(makeList(this, values));
    }

    public set(index: number, value: T): this {
        const values = this._values.slice();
        values[index] = value;
        return this.update(values);
    }

    /**
     * Returns a new List with value at index with a size 1 more than this List.
     * Values at indices above index are shifted over by 1.
     */
    public insert(index: number, value: T): this {
        if (index < 0 || index > this.size) {
            throw new Error(`Index ${index} is out of bounds (size ${this.size})`);
        }

        return this.splice(index, 0, value);
    }

    /**
     * Returns a new List which excludes this index and with a size 1 less than this List.
     * Values at indices above index are shifted down by 1 to fill the position.
     */
    public delete(index: number): this {
        return this.splice(index, 1);
    }

    /**
     * Returns true if this List includes no values.
     */
    public isEmpty(): boolean {
        return this._values.length === 0;
    }

    /**
     * Return an empty list of the same type.
     */
    public clear(): this {
        // @ts-ignore
        return this.constructor.create();
    }

    /**
     * In case the List is not empty returns the first element of the List.
     * In case the List is empty returns the optional default value if provided,
     * if no default value is provided returns undefined.
     */
    public first(defaultValue: T | null | undefined = undefined): T | null | undefined {
        return this._values[0] || defaultValue;
    }

    /**
     * In case the List is not empty returns the last element of the List.
     * In case the List is empty returns the optional default value if provided,
     * if no default value is provided returns undefined.
     */
    public last(defaultValue: T | null | undefined = undefined): T | null | undefined {
        return this._values[this._values.length - 1] || defaultValue;
    }

    public push(value: T): this {
        return makeList(this, this._values.concat([value]));
    }

    /**
     * Returns a new List of the same type containing all entries except the first.
     */
    public rest(): this {
        return this.slice(1);
    }

    public pop(): this {
        return makeList(this, this._values.slice(0, -1));
    }

    /**
     * Returns a new List with the provided values prepended, shifting other values ahead to higher indices.
     */
    public unshift(...items: T[]): this {
        return this.withMutations((array) => {
            array.unshift(...items);
        });
    }

    public shift(): this {
        return this.withMutations((array) => {
            array.shift();
        });
    }

    public slice(start: number, end?: number): this {
        if (start === 0 && end && end >= this.size) {
            return this;
        }

        return this.update(this._values.slice(start, end));
    }

    public take(amount: number): this {
        return this.slice(0, Math.max(0, amount));
    }

    public takeLast(amount: number): this {
        return this.slice(-Math.max(0, amount));
    }

    public skip(amount: number): this {
        return amount === 0 ? this : this.slice(Math.max(0, amount));
    }

    public splice(start: number, deleteCount?: number, ...items: T[]): this {
        return this.withMutations((array) => {
            array.splice(start, deleteCount || 0, ...items);
        });
    }

    public concat(...items: (T[] | ImmutableList<T> | Iterable<T>)[]): this {
        return this.update(
            this._values.concat(
                ...items.map((item) => {
                    if (ImmutableList.isList(item)) {
                        return item._values;
                    }
                    if (isIterable(item)) {
                        return Array.from(item);
                    }
                    return item;
                })
            )
        );
    }

    /**
     * Joins values together as a string, inserting a separator between each. The default separator is ",".
     */
    public join(separator = ','): string {
        return this._values.join(separator);
    }

    /**
     * Returns a new List of the same type in reverse order.
     */
    public reverse(): this {
        return this.withMutations((array) => {
            array.reverse();
        });
    }

    /**
     * Creates a new list with all sub-array/list elements concatenated into it recursively up to the specified depth.
     */
    public flat(depth: number = Number.POSITIVE_INFINITY): this {
        const array = this.toArray();
        const flat = flatten(array, depth);

        return this.update(flat);
    }

    /**
     * Creates a new list populated with the results of calling a provided function on every element in the calling list.
     */
    public map<U>(
        callback: (value: T, index: number, array: ImmutableList<T>) => U
    ): ImmutableList<U> {
        // @ts-ignore
        return this.constructor.create(
            this._values.map((item, index) => callback(item, index, this))
        );
    }

    public flatMap<U>(
        callback: (value: T, index: number, array: ImmutableList<T>) => U[]
    ): ImmutableList<U> {
        // @ts-ignore
        return this.constructor.create(
            this._values.flatMap((item, index) => callback(item, index, this))
        );
    }

    /**
     * Returns the first value for which the predicate returns true.
     */
    public find(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): T | undefined {
        return this._values.find((item, index) => callback(item, index, this));
    }

    /**
     * Returns the first index in the List where a value satisfies the provided predicate function.
     * Otherwise -1 is returned.
     */
    public findIndex(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): number {
        return this._values.findIndex((item, index) => callback(item, index, this));
    }

    public filter(
        predicate: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): ImmutableList<T> {
        return this.update(this._values.filter((item, index) => predicate(item, index, this)));
    }

    public reduce<U>(
        callback: (
            previousValue: U,
            currentValue: T,
            currentIndex: number,
            array: ImmutableList<T>
        ) => U,
        initialValue: U
    ): U {
        return this._values.reduce(
            (previousValue, currentValue, currentIndex) =>
                callback(previousValue, currentValue, currentIndex, this),
            initialValue
        );
    }

    public reduceRight<U>(
        callback: (
            previousValue: U,
            currentValue: T,
            currentIndex: number,
            array: ImmutableList<T>
        ) => U,
        initialValue: U
    ): U {
        return this._values.reduceRight(
            (previousValue, currentValue, currentIndex) =>
                callback(previousValue, currentValue, currentIndex, this),
            initialValue
        );
    }

    public every(callback: (value: T, index: number, array: ImmutableList<T>) => boolean): boolean {
        return this._values.every((item, index) => callback(item, index, this));
    }

    public some(callback: (value: T, index: number, array: ImmutableList<T>) => boolean): boolean {
        return this._values.some((item, index) => callback(item, index, this));
    }

    /**
     * Returns a new List of the same type which includes entries starting from when predicate first returns true.
     */
    public skipUntil(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): this {
        const index = this.findIndex(callback);
        return index === -1 ? this.clear() : this.skip(index);
    }

    /**
     * Returns a new List of the same type which includes entries starting from when predicate first returns false.
     */
    public skipWhile(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): this {
        const index = this.findIndex((value, index, array) => !callback(value, index, array));
        return index === -1 ? this.clear() : this.skip(index);
    }

    /**
     * Returns a new List of the same type which includes entries from this Collection as long as the predicate returns true.
     */
    public takeWhile(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): this {
        const index = this.findIndex((value, index, array) => !callback(value, index, array));
        return index === -1 ? this : this.take(index);
    }

    /**
     * Returns a new List of the same type which includes entries from this List as long as the predicate returns false.
     */
    public takeUntil(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): this {
        return this.takeWhile((value, index, array) => !callback(value, index, array));
    }

    /**
     * The `callback` is executed for every entry in the Collection.
     * Unlike Array#forEach, if any call of sideEffect returns false, the iteration will stop.
     * Returns the number of entries iterated (including the last iteration which returned false).
     */
    public forEach(callback: (value: T, index: number, array: ImmutableList<T>) => any): number {
        let iterations = 0;

        for (let index = 0; index < this._values.length; index++) {
            iterations++;

            // @ts-ignore
            if (callback(this._values[index], index, this) === false) {
                break;
            }
        }

        return iterations;
    }

    /**
     * Like sort, but also accepts a comparatorValueMapper which allows for sorting by more sophisticated means.
     */
    public sortBy<U>(comparatorValueMapper: (value: T) => U): this {
        return this.update(
            this._values.slice().sort((a, b) => {
                const aValue = comparatorValueMapper(a);
                const bValue = comparatorValueMapper(b);

                if (aValue < bValue) {
                    return -1;
                }

                if (aValue > bValue) {
                    return 1;
                }

                return 0;
            })
        );
    }

    public indexOf(searchElement: T, fromIndex?: number): number {
        return this._values.indexOf(searchElement, fromIndex);
    }

    public lastIndexOf(searchElement: T, fromIndex?: number): number {
        return this._values.lastIndexOf(searchElement, fromIndex);
    }

    public includes(searchElement: T, fromIndex?: number): boolean {
        return this._values.includes(searchElement, fromIndex);
    }

    /**
     * Returns the last value for which the predicate returns true.
     */
    public findLast(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): T | undefined {
        for (let index = this._values.length - 1; index >= 0; index--) {
            // @ts-ignore
            if (callback(this._values[index], index, this)) {
                return this._values[index];
            }
        }

        return undefined;
    }

    /**
     * Returns the last [key, value] entry for which the predicate returns true.
     */
    public findLastEntry(
        callback: (value: T, index: number, array: ImmutableList<T>) => unknown
    ): [number, T] | undefined {
        for (let index = this._values.length - 1; index >= 0; index--) {
            // @ts-ignore
            if (callback(this._values[index], index, this)) {
                // @ts-ignore
                return [index, this._values[index]];
            }
        }

        return undefined;
    }

    /**
     * Returns a new List of the same type which includes unique entries.
     */
    public unique(): this {
        return this.update(
            this._values.filter((item, index, array) => array.indexOf(item) === index)
        );
    }

    /**
     * Return a new List of the same type which includes unique entries based on the isEqual function.
     */
    public uniqueBy(isEqual: (lhs: T, rhs: T) => boolean): this {
        return this.update(
            this._values.filter((item, index, array) => {
                const firstMatch = array.findIndex((rhs) => isEqual(item, rhs));
                return firstMatch === index;
            })
        );
    }

    public toString(): string {
        return `List<${typeNameOf(this.first())}>{${this.size}}`;
    }

    /**
     * Execute a function on a mutable version of the list.
     */
    public withMutations(change: (mutableArray: Array<T>) => void): this {
        const copy = this.toArray();
        change(copy);
        return makeList(this, copy);
    }

    public toArray(): T[] {
        return this._values.slice();
    }

    public toKeysArray(): number[] {
        return [...this._values.keys()];
    }

    public toValuesArray(): T[] {
        return this.toArray();
    }

    public toEntriesArray(): [number, T][] {
        return this.toArray().map((value, index) => [index, value]);
    }

    public iterate(fn: CollectionIterateFn<number, T>): void {
        this._values.forEach(fn);
    }

    public equals(other: any): boolean {
        if (this === other) {
            return true;
        }

        if (this.constructor !== other.constructor || this.size !== other.size) {
            return false;
        }

        if (hashCollection(this) !== hashCollection(other)) {
            return false;
        }

        return this.every((value, index) => equals(value, other.get(index)));
    }

    public hashCode(): number {
        return hashCollection(this);
    }

    /**
     * Iterator for the values in the list.
     */
    *[Symbol.iterator]() {
        yield* this._values;
    }
}

/**
 * For fast type-checking.
 * We store it on the prototype so that it's not enumerable and will be copied over by makeList.
 */
// @ts-ignore
ImmutableList.prototype[COLLECTION_TYPE_SYMBOL] = LIST_TYPE_SYMBOL;

function flatten<T>(
    input: Array<T | Array<T> | ImmutableList<T>>,
    depth: number = Number.POSITIVE_INFINITY
): Array<T> {
    const result: Array<T> = [];
    input.forEach((item) => {
        if (item instanceof ImmutableList) {
            result.push(...flatten(item.toArray(), depth - 1));
        } else if (Array.isArray(item)) {
            result.push(...flatten(item, depth - 1));
        } else {
            result.push(item);
        }
    });

    return result;
}

/**
 * Fast constructor for new list entries as "new X" is slower.
 */
function makeList<V, T extends ImmutableList<V>>(likeList: T, values: V[]): T {
    const record = Object.create(Object.getPrototypeOf(likeList));
    record._values = values;
    record.size = values.length;
    return record;
}
